/* eslint-disable perfectionist/sort-objects */
import { invert } from 'remeda';
import { match, P } from 'ts-pattern';

import type { GoogleFont } from '@core/interfaces/IFont';

export const WEIGHT_TO_STYLE_MAP = {
  100: 'Thin',
  200: 'Extra Light',
  300: 'Light',
  400: 'Regular',
  500: 'Medium',
  600: 'Semi Bold',
  700: 'Bold',
  800: 'Extra Bold',
  900: 'Black',
} as const;

export const WEIGHT_TO_POSTSCRIPT_MAP = {
  100: 'Thin',
  200: 'ExtraLight',
  300: 'Light',
  400: 'Regular',
  500: 'Medium',
  600: 'SemiBold',
  700: 'Bold',
  800: 'ExtraBold',
  900: 'Black',
} as const;

export const POSTSCRIPT_TO_WEIGHT_MAP = invert(WEIGHT_TO_POSTSCRIPT_MAP);

export const FONT_FALLBACK_FAMILIES = ['PingFang TC', 'Arial', 'Times New Roman', 'Ubuntu', 'Noto Sans'] as const;

export type FontWeight = keyof typeof WEIGHT_TO_STYLE_MAP;

/**
 * Utility functions for font constant operations
 */

/**
 * Generate PostScript name for Google Fonts
 * @param family - Font family name (e.g., "Inter", "Roboto")
 * @param weight - Font weight (100-900)
 * @param italic - Whether the font is italic
 * @returns PostScript name (e.g., "Inter-Bold", "Inter-BoldItalic")
 */
export const generateGoogleFontPostScriptName = (family: string, weight: number, italic: boolean): string => {
  const cleanFamily = family.replace(/\s+/g, '');
  const weightStyle = WEIGHT_TO_POSTSCRIPT_MAP[weight as FontWeight] || 'Regular';
  const suffix = italic ? `${weightStyle}Italic` : weightStyle;

  return `${cleanFamily}-${suffix}`;
};

/**
 * Generate human-readable style name from weight and italic
 * @param weight - Font weight (100-900)
 * @param italic - Whether the font is italic
 * @returns Style name (e.g., "Bold", "Bold Italic", "Regular")
 */
export const generateStyleFromWeightAndItalic = (weight: number, italic: boolean): string => {
  const weightName = WEIGHT_TO_STYLE_MAP[weight as FontWeight] || 'Regular';

  return italic ? `${weightName} Italic` : weightName;
};

export const getWeightAndStyleFromVariant = (variant: string) =>
  match(variant)
    .with('regular', () => ({ italic: false, style: 'Regular', weight: 400 }))
    .with('italic', () => ({ italic: true, style: 'Italic', weight: 400 }))
    .with(P.string.endsWith('italic'), (v) => {
      const weight = Number.parseInt(v.replace('italic', ''), 10) as FontWeight;
      const style = WEIGHT_TO_STYLE_MAP[weight] || 'Italic';

      return { italic: true, style: `${style} Italic`, weight };
    })
    .with(P.string.regex(/^\d+$/), (v) => {
      const weight = Number.parseInt(v, 10) as FontWeight;
      const style = WEIGHT_TO_STYLE_MAP[weight] || 'Regular';

      return { italic: false, style, weight };
    })
    .otherwise(() => ({ italic: false, style: 'Regular', weight: 400 }));

export const createGoogleFontObject = ({
  fontFamily,
  weight = 400,
  style = 'Regular',
  binaryLoader,
}: {
  binaryLoader: (fontFamily: string, weight?: number, style?: 'italic' | 'normal') => Promise<ArrayBuffer | null>;
  fontFamily: string;
  style?: string;
  weight?: number;
}): GoogleFont => {
  const italic = /italic/i.test(style);

  return {
    binaryLoader,
    family: fontFamily,
    italic,
    postscriptName: generateGoogleFontPostScriptName(fontFamily, weight, italic),
    source: 'google',
    style,
    weight,
  };
};
